import React, { Reducer, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { PostScheduleView, PostScheduleViewProps } from './PostSchedule.view';
import {
  BulkDeletePostsMutationFn,
  BulkUpdatePostInput,
  BulkUpdatePostsMutationFn,
  CoreCategoryFieldsFragment,
  PostItemFragment,
  GetCategoryProfilesQueueMetaLazyQueryHookResult,
  GetCategoryProfilesQueueMetaQueryResult,
  GetPostFamilyLazyQueryHookResult,
  GetPostFamilyQueryResult,
  GetPostIterationsLazyQueryHookResult,
  GetPostIterationsQueryResult,
  GetPostScheduleListDocument,
  PostInputStatus,
  PostOrderUpdateDirection,
  PostQueuePosition,
  PostSource,
  PostStatus,
  PostTiming,
  QueueFilterInput,
  QueueFilterPostContent,
  QueueFilterPostEvergreen,
  QueueFilterPostSource,
  QueueFilterPostStatus,
  QueueFilterPostTiming,
  QueueMetaFieldsFragment,
  ShuffleQueueMutationFn,
  SocialProfileFieldsFragment,
  StartOfWeekFormatSetting,
  TimezoneSetting,
  UpdatePauseSettingsMutationFn,
  UpdatePostMutationFn,
  UpdatePostOrderMutationFn,
  PlanUsageType,
  UsageFieldsFragmentDoc
} from '../../../models/graphql';
import { useDimensions } from '../../../hooks/useDimensions';
import { BreakpointsValuesEnum } from '../../../constants/breakpoints-values';
import { CardOrientationEnum } from '../../../components/cards/BasePostCard/BasePostCard.view';
import { PostScheduleFilterAction, PostScheduleFilterType, QueueFiltersType } from '../../../models/post';
import { PostScheduleHelpers } from './postScheduleHelpers';
import { ApolloError, useApolloClient } from '@apollo/client';
import { AppContext } from '../../../providers/AppProvider';
import { useForm, useWatch } from 'react-hook-form';
import { FormOptions } from '../../../constants/form';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import {
  getFilterLocalStorageValue,
  PostScheduleFiltersEnum,
  setFilterLocalStorage
} from '../../../helpers/filterStorageHelpers';
import { showToast } from '../../../helpers/showToast';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { ContainerListItem } from '../../../components/actions/ListItem/ListItem.view';
import { addWeeks, endOfWeek, startOfWeek } from 'date-fns';
import { useSettings } from 'hooks/useSettings';
import { PostEditorContextEnum } from '../../../models/post-editor';
import { PostScheduleContext } from '../../../providers/PostScheduleProvider';
import { getUrlParams } from '../../../helpers/getUrlParams';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { TimezoneNames } from '../../../constants/timezones';
import { convertTimezoneEnumToName } from '../../../helpers/timeFormatter';
import { utcToZonedTime } from 'date-fns-tz';
import { statisticsCondition, statisticsConditionType } from 'components/cards/helpers/getStatisticsCondition';
import { isGetPostScheduleListQuery } from './PostScheduleList/PostScheduleList.apollo';
import { usePauseSettings } from '../../../hooks/usePauseSettings';
import { GetCategories_workspace_categories } from '../../../models/legacy-graphql';
import { ContainerListItemExtended } from '../../../components/actions/ButtonMenu/ButtonMenu.view';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';
import { OnboardingContext } from '../../Onboarding/states/OnboardingProvider';
import { OnboardingStaticContent } from '../../Onboarding/constants/content';

export type DuplicateOrReschedulePostType = 'duplicate' | 'reschedule';
export type DuplicateOrReschedulePostHandler = (post: PostItemFragment, type: DuplicateOrReschedulePostType) => void;

export type PostScheduleControllerProps = {
  loadingMore?: boolean;
  context: PostScheduleViewContext;
  setContext: (context: PostScheduleViewContext) => void;
  categories: CoreCategoryFieldsFragment[];
  socialProfiles: SocialProfileFieldsFragment[];
  queueMeta: QueueMetaFieldsFragment[];
  getGetPostFamily: GetPostFamilyLazyQueryHookResult[0];
  postFamilyResults: GetPostFamilyQueryResult;
  getPostIterations: GetPostIterationsLazyQueryHookResult[0];
  postIterationsResults: GetPostIterationsQueryResult;
  getCategoryProfilesQueueMeta: GetCategoryProfilesQueueMetaLazyQueryHookResult[0];
  categoryProfilesQueueMetaResults: GetCategoryProfilesQueueMetaQueryResult;
  bulkDeletePosts: BulkDeletePostsMutationFn;
  updatePost: UpdatePostMutationFn;
  bulkUpdatePosts: BulkUpdatePostsMutationFn;
  updateWorkspacePauseSettings: UpdatePauseSettingsMutationFn;
  shuffleQueue: ShuffleQueueMutationFn;
  updatePostOrder: UpdatePostOrderMutationFn;
};

export enum ActionFocus {
  SELECTED = 'SELECTED',
  ALL = 'ALL'
}

export enum PostScheduleViewContext {
  LIST = 'LIST',
  WEEK = 'WEEK',
  LIBRARY = 'LIBRARY'
}

export type QueueModalStateType = {
  state: boolean;
  setter: React.Dispatch<React.SetStateAction<boolean>>;
};

export type QueueModalPackageType = {
  filter: QueueModalStateType;
  delete: QueueModalStateType;
  pause: QueueModalStateType;
  shuffle: QueueModalStateType;
  details: QueueModalStateType;
};

export type DateRanges = {
  startDate: Date;
  endDate: Date;
};

export type PostPauseStatusType = { categoryId: string | number; profileId: string | number; isPaused: boolean };

export const isPostScheduleFilterType = (obj: unknown): obj is PostScheduleFilterType => {
  const record = obj as Record<string, unknown>;

  return (
    typeof record.status === 'string' &&
    typeof record.profile === 'string' &&
    typeof record.category === 'string' &&
    typeof record.timing === 'string' &&
    typeof record.evergreen === 'string' &&
    typeof record.source === 'string' &&
    typeof record.content === 'string' &&
    typeof record.fetchPosts === 'boolean'
  );
};

const getCurrentRanges = (dayStartOfWeek: StartOfWeekFormatSetting, tz: string): DateRanges => {
  const currentZonedDate = utcToZonedTime(new Date(), tz);
  const startDate =
    StartOfWeekFormatSetting.SUNDAY === dayStartOfWeek
      ? startOfWeek(currentZonedDate, { weekStartsOn: 0 })
      : startOfWeek(currentZonedDate, { weekStartsOn: 1 });
  const endDate =
    StartOfWeekFormatSetting.SUNDAY === dayStartOfWeek
      ? endOfWeek(currentZonedDate, { weekStartsOn: 0 })
      : endOfWeek(currentZonedDate, { weekStartsOn: 1 });
  return { startDate, endDate };
};

export const getBitlyMenuItems = (link: PostItemFragment['link']): ContainerListItem[] => {
  return [
    ...((link?.urlBitlink || link?.initialUrlBitlink) && !!link?.url
      ? [
          {
            label: 'View Bitly stats',
            action: () =>
              window.open(
                `//app.bitly.com/default/links/bit.ly/${PostScheduleHelpers.getBitlyCodeFromLinkUrl(
                  link?.url || '' // TS does not recognise the condition for this, so need to make it happy
                )}/details`,
                '_blank'
              )
          },
          {
            label: 'View original link',
            action: () => window.open(String(link?.expandedUrl), '_blank')
          }
        ]
      : [])
  ];
};

type EditPostHandlerType = (post: PostItemFragment) => void;
export type PostPreviewType = { post: PostItemFragment; editPost: EditPostHandlerType };

export const PostScheduleController = (props: PostScheduleControllerProps): JSX.Element => {
  const { cache } = useApolloClient();
  const { selectedWorkspaceId, setPostEditorContext, setLimitReachedModalState, setSuspendedModalOpen } =
    useContext(AppContext);
  const { showOnboarding, state: onboardingState } = useContext(OnboardingContext);
  const { version } = onboardingState;
  const disableDelete = OnboardingStaticContent[`v${version}`].disableDelete.posts;
  const { setLoadingInitial } = useContext(PostScheduleContext);
  const location = useLocation();
  const { state: locationState } = location;

  const {
    context,
    setContext,
    updatePostOrder,
    bulkDeletePosts,
    bulkUpdatePosts,
    getCategoryProfilesQueueMeta,
    getPostIterations,
    shuffleQueue
  } = props;
  const bodyRef = useRef(document.body);
  const { width } = useDimensions(bodyRef);
  const [orientation, setOrientation] = useState<CardOrientationEnum>(CardOrientationEnum.LANDSCAPE);
  const [hideFilters, setHideFilters] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openPauseModal, setOpenPauseModal] = useState<boolean>(false);
  const [openShuffleModal, setOpenShuffleModal] = useState<boolean>(false);
  const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);
  const [showPostPreview, setShowPostPreview] = useState<null | PostPreviewType>(null);
  const [actionFocus, setActionFocus] = useState<ActionFocus>(ActionFocus.SELECTED);
  const { selectedOrgSettings, selectedWorkspaceSettings } = useSettings();
  const { selectedWorkspace } = useSelectedOrganization();
  const wsTz: TimezoneNames =
    convertTimezoneEnumToName(selectedWorkspace?.timezone || TimezoneSetting.UTC) || TimezoneNames.UTC;
  const dayStartOfWeek = selectedOrgSettings?.startOfWeekFormat ?? StartOfWeekFormatSetting.MONDAY;
  const [dateRanges, setDateRanges] = useState<DateRanges>(getCurrentRanges(dayStartOfWeek, wsTz));
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const [showCategoryEditModal, setShowCategoryEditModal] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<GetCategories_workspace_categories | undefined>(undefined);
  const { canAddPosts } = useOrganizationPlan();
  const { isTemporallySuspended } = useSubscriptionStatus();
  const [postIdToDelete, setPostIdToDelete] = useState<string | undefined>(undefined);

  const { isSocialProfilePausedForCategory, isCategorySeasonallyPaused } = usePauseSettings();

  const getPostIdsFromWorkspaceQueueCursorPagination = useCallback(() => {
    if (!selectedWorkspaceId) {
      return [];
    }

    const data = apolloClient.readQuery({
      query: GetPostScheduleListDocument,
      variables: { workspaceId: selectedWorkspaceId }
    });

    if (isGetPostScheduleListQuery(data)) {
      return (data.workspace.queue.cursorPagination?.items || []).map(post => post.id);
    }

    return [];
  }, [apolloClient, selectedWorkspaceId]);

  const { setRefetch } = useContext(PostScheduleContext);

  const queueModals: QueueModalPackageType = {
    filter: {
      state: showFilterModal,
      setter: setShowFilterModal
    },
    delete: {
      state: openDeleteModal,
      setter: setOpenDeleteModal
    },
    pause: {
      state: openPauseModal,
      setter: setOpenPauseModal
    },
    shuffle: {
      state: openShuffleModal,
      setter: setOpenShuffleModal
    },
    details: {
      state: openDetailsModal,
      setter: setOpenDetailsModal
    }
  };

  const formInstance = useForm({
    ...FormOptions,
    mode: 'onSubmit',
    defaultValues: {
      term: searchTerm,
      sortBy: 'LATEST'
    }
  });

  const checkboxes = useWatch({
    control: formInstance.control,
    name: 'postItemCheckBox'
  });

  const selectedPosts = PostScheduleHelpers.getSelectedCheckboxes(checkboxes);

  const categoryPauseStatus: PostPauseStatusType[] = [];
  props.categoryProfilesQueueMetaResults?.data?.workspace.queueMeta.forEach(m => {
    if (m.category && m.socialProfile) {
      categoryPauseStatus.push({
        categoryId: m.category.id || 0,
        profileId: m.socialProfile?.id || 0,
        isPaused: m.paused
      });
    }
  });

  const onSubmit: SubmitHandler<{ term: string }> = (data: { term: string }, ev) => {
    ev?.preventDefault();
    setSearchTerm(data.term);
  };

  const setDateRange = useCallback(
    (amount: number) => {
      if (amount === 0) {
        setDateRanges(getCurrentRanges(dayStartOfWeek, wsTz));
        return;
      } else {
        setDateRanges(({ startDate, endDate }) => ({
          startDate: addWeeks(startDate, amount),
          endDate: addWeeks(endDate, amount)
        }));
      }
    },
    [setDateRanges, dayStartOfWeek, wsTz]
  );

  const filterGroup = useMemo(() => {
    return context === PostScheduleViewContext.LIBRARY ? 'PostLibraryFilters' : 'PostScheduleFilters';
  }, [context]);

  const filtersInitialState = useMemo(
    () => ({
      status:
        getFilterLocalStorageValue(
          selectedWorkspaceId,
          filterGroup,
          PostScheduleFiltersEnum.postScheduleStatusFilter
        ) || QueueFilterPostStatus.SCHEDULED,
      profile: getFilterLocalStorageValue(
        selectedWorkspaceId,
        filterGroup,
        PostScheduleFiltersEnum.postScheduleProfileFilter
      ),
      category: getFilterLocalStorageValue(
        selectedWorkspaceId,
        filterGroup,
        PostScheduleFiltersEnum.postScheduleCategoryFilter
      ),
      timing: getFilterLocalStorageValue(
        selectedWorkspaceId,
        filterGroup,
        PostScheduleFiltersEnum.postScheduleTimingFilter
      ),
      evergreen: getFilterLocalStorageValue(
        selectedWorkspaceId,
        filterGroup,
        PostScheduleFiltersEnum.postScheduleEvergreenFilter
      ),
      source: getFilterLocalStorageValue(
        selectedWorkspaceId || '',
        filterGroup,
        PostScheduleFiltersEnum.postScheduleSourceFilter
      ),
      content: getFilterLocalStorageValue(
        selectedWorkspaceId || '',
        filterGroup,
        PostScheduleFiltersEnum.postScheduleContentFilter
      ),
      fetchPosts: true
    }),
    [filterGroup, selectedWorkspaceId]
  );

  const [filters, dispatchFilters] = useReducer<Reducer<PostScheduleFilterType, PostScheduleFilterAction>>(
    PostScheduleHelpers.postScheduleFilterReducer,
    filtersInitialState
  );

  const { category, content, profile, source, status, timing, evergreen, fetchPosts } = filters;

  // When width is below medium, we force the use of List View
  const forceListView = useMemo(() => {
    return width < BreakpointsValuesEnum.medium;
  }, [width]);

  const updateAllCheckBoxValues = useCallback(
    (checkBoxName: string, value: boolean) => {
      const formValues: Array<string | boolean> = formInstance.getValues(checkBoxName);
      const updatedValues: Array<string | boolean> = [];

      // Only get values of we are set to true
      if (value) {
        formValues?.forEach((_item, index) => {
          updatedValues[index] = value;
        });
      }

      formInstance.setValue(checkBoxName, updatedValues);
    },
    [formInstance]
  );

  const actOnAllPosts = useCallback(
    (callback: CallableFunction) => {
      updateAllCheckBoxValues('postItemCheckBox', true);
      callback();
    },
    [updateAllCheckBoxValues]
  );

  const {
    setTriggerPostReFetch,
    setTriggerReShuffleFetch,
    setTriggerDeleteReFetch,
    setFirstInViewId,
    visiblePostIdsRef
  } = useContext(PostScheduleContext);

  const { updatePost } = props;
  const movePostPosition = useCallback(
    (postId: string, position: PostQueuePosition) => {
      // Todo, set the flashPostId so we know where to return
      updatePost({
        variables: {
          postId: postId,
          input: { timing: { type: PostTiming.QUEUE, queue: { queuePosition: position } } }
        },
        update: () => {
          updateAllCheckBoxValues('postItemCheckBox', false);
        }
      })
        .then(() => {
          const firstObject = visiblePostIdsRef?.current.filter(post => post.postId !== postId)[0];
          setFirstInViewId(firstObject?.postId || postId);

          setTriggerPostReFetch(postId);
        })
        .catch((error: ApolloError) => {
          showToast({
            severity: 'error',
            summary: 'Could not move the post',
            detail: remapApolloErrors(error)
          });
        });
    },
    [setFirstInViewId, setTriggerPostReFetch, updateAllCheckBoxValues, updatePost, visiblePostIdsRef]
  );

  const updatePostLocation = useCallback(
    async (postId: string, refPostId: string, direction: PostOrderUpdateDirection) => {
      if (postId === refPostId) return;

      await updatePostOrder({
        variables: {
          postId: postId,
          relativePostId: refPostId,
          direction: direction
        }
      }).catch((error: ApolloError) => {
        showToast({
          severity: 'error',
          summary: 'Could not move the post',
          detail: remapApolloErrors(error)
        });
      });
    },
    [updatePostOrder]
  );

  const queueFilters: QueueFiltersType = useMemo(
    () => ({
      status: Boolean(status)
        ? QueueFilterPostStatus[status as QueueFilterPostStatus]
        : QueueFilterPostStatus.SCHEDULED,
      searchTerm,
      socialProfileId: Boolean(profile) ? profile : undefined,
      categoryId: Boolean(category) ? category : undefined,
      timing: Boolean(timing) ? QueueFilterPostTiming[timing as QueueFilterPostTiming] : null,
      evergreen: Boolean(evergreen) ? QueueFilterPostEvergreen[evergreen as QueueFilterPostEvergreen] : null,
      source: Boolean(source) ? QueueFilterPostSource[source as QueueFilterPostSource] : null,
      content: Boolean(content) ? QueueFilterPostContent[content as QueueFilterPostContent] : null,
      fetchPosts
    }),
    [category, content, evergreen, fetchPosts, profile, searchTerm, source, status, timing]
  );

  const updatePostsStatus = useCallback(
    (ids: string[], status: PostInputStatus) => {
      const updateInput: BulkUpdatePostInput[] = ids.map(id => ({
        postId: id,
        postInput: { status }
      }));

      bulkUpdatePosts({
        variables: { input: updateInput },
        update: () => {
          if (status !== PostInputStatus.TRASH) return; // we only evict for the trashed posts
          ids.forEach(id => {
            cache.evict({ id: cache.identify({ id, __typename: 'Post' }) });
          });
          cache.gc();

          updateAllCheckBoxValues('postItemCheckBox', false);
        }
      })
        .then(() => {
          if (status === PostInputStatus.TRASH) {
            const trashMessage = ids.length > 1 ? 'Posts moved to Trash' : 'Post moved to Trash';

            showToast({
              severity: 'success',
              summary: '',
              detail: trashMessage
            });
          }

          // The refetch will also set loader to false
          setTriggerDeleteReFetch(true);
        })
        .catch((error: ApolloError) => {
          const summary =
            status === PostInputStatus.TRASH ? 'Could not delete your Post' : 'Could not restore your Post';
          showToast({
            severity: 'error',
            summary,
            detail: remapApolloErrors(error)
          });
        });
    },
    [bulkUpdatePosts, cache, setTriggerDeleteReFetch, updateAllCheckBoxValues]
  );

  const editPost: EditPostHandlerType = useCallback(
    (post: PostItemFragment) => {
      if (isTemporallySuspended) {
        setSuspendedModalOpen(true);
        return;
      }
      setPostEditorContext({
        type: PostEditorContextEnum.edit,
        postId: post.id,
        onClose: success => {
          if (success) {
            setTriggerPostReFetch(post.id);
          }
        }
      });
      setShowPostPreview(null);
    },
    [setPostEditorContext, setTriggerPostReFetch, isTemporallySuspended, setSuspendedModalOpen]
  );

  const restorePosts = useCallback(
    (postIds: string[]) => {
      const ids: string[] = postIds.length === 0 ? getPostIdsFromWorkspaceQueueCursorPagination() : postIds;
      updatePostsStatus(ids, PostInputStatus.ACTIVE);
    },
    [getPostIdsFromWorkspaceQueueCursorPagination, updatePostsStatus]
  );

  const duplicateOrReschedulePost: DuplicateOrReschedulePostHandler = useCallback(
    (post, type) => {
      if (isTemporallySuspended) {
        setSuspendedModalOpen(true);
        return;
      }
      if (!canAddPosts(1)) {
        setLimitReachedModalState({ isOpen: true, type: 'posts' });
        return;
      }
      // reactivating past -> pass in familyUuid and parentId
      // making a new in edit mode -> familyUuid
      // making a new from scratch -> none
      const failedPost = post.status === PostStatus.FAILED;
      let postEditorContext:
        | PostEditorContextEnum.duplicate
        | PostEditorContextEnum.reschedule_edit
        | PostEditorContextEnum.reschedule_new;
      if (type === 'reschedule' && failedPost) {
        postEditorContext = PostEditorContextEnum.reschedule_edit;
      } else {
        postEditorContext =
          type === 'duplicate' ? PostEditorContextEnum.duplicate : PostEditorContextEnum.reschedule_new;
      }

      const onClose = (success: boolean) => {
        if (success) {
          setTriggerPostReFetch(post.id);
        }
      };

      if (postEditorContext === PostEditorContextEnum.reschedule_edit) {
        setPostEditorContext({
          type: postEditorContext,
          postId: post.id,
          onClose
        });
      } else {
        setPostEditorContext({
          type: postEditorContext,
          postId: post.id,
          sourceType: type === 'duplicate' ? PostSource.DUPLICATE : PostSource.RESCHEDULE,
          overrideWorkspaceSettings: true,
          onClose
        });
      }
    },
    [
      setTriggerPostReFetch,
      setPostEditorContext,
      canAddPosts,
      setLimitReachedModalState,
      isTemporallySuspended,
      setSuspendedModalOpen
    ]
  );

  const unPausePosts = useCallback(
    (post: PostItemFragment) => {
      if (post.category.seasonallyPaused) {
        setSelectedCategory(() => post.category as unknown as GetCategories_workspace_categories);
        setShowCategoryEditModal(() => true);
      } else {
        navigate({ pathname: '/settings/pause' });
      }
    },
    [navigate]
  );

  const trashPosts = useCallback(
    (postIds: string[]) => {
      const ids: string[] = postIds.length === 0 ? getPostIdsFromWorkspaceQueueCursorPagination() : postIds;
      updatePostsStatus(ids, PostInputStatus.TRASH);
    },
    [getPostIdsFromWorkspaceQueueCursorPagination, updatePostsStatus]
  );

  const deleteSinglePost = useCallback(
    (id: string) => {
      setPostIdToDelete(id);
      queueModals.delete.setter(true);
    },
    [queueModals.delete]
  );

  const deletePosts = useCallback(
    (postIds: string[], action: 'Trash' | 'Delete' | 'Restore') => {
      const ids: string[] = postIds.length === 0 ? getPostIdsFromWorkspaceQueueCursorPagination() : postIds;

      // If we are processing deletes, always make sure the single is undefined
      setPostIdToDelete(undefined);

      if (action === 'Delete') {
        bulkDeletePosts({
          variables: { postIds: ids },
          update: () => {
            updateAllCheckBoxValues('postItemCheckBox', false);
          }
        })
          .then(() => {
            showToast({
              severity: 'success',
              summary: '',
              detail: ids.length > 1 ? 'Posts deleted permanently' : 'Post deleted permanently'
            });

            // The refetch will also set loader to false
            setTriggerDeleteReFetch(true);
            const oldUsage: PlanUsageType | null = cache.readFragment({
              fragment: UsageFieldsFragmentDoc,
              fragmentName: 'UsageFields',
              id: 'PlanUsageType:{}'
            });
            const newUsage = { ...oldUsage, posts: (oldUsage?.posts ?? 0) - ids.length };
            cache.writeFragment({
              fragment: UsageFieldsFragmentDoc,
              fragmentName: 'UsageFields',
              id: 'PlanUsageType:{}',
              data: newUsage
            });
          })
          .catch((error: ApolloError) => {
            showToast({
              severity: 'error',
              summary: 'Could not delete your post',
              detail: remapApolloErrors(error)
            });
          });
      } else if (action === 'Trash') {
        trashPosts(ids);
      } else if (action === 'Restore') {
        restorePosts(ids);
      }
    },
    [
      bulkDeletePosts,
      getPostIdsFromWorkspaceQueueCursorPagination,
      restorePosts,
      setTriggerDeleteReFetch,
      trashPosts,
      updateAllCheckBoxValues,
      cache
    ]
  );

  const showDetailsModalHandler = useCallback(
    (postId: string) => {
      getPostIterations({ variables: { postId } }).finally();
      setOpenDetailsModal(() => true);
    },
    [getPostIterations]
  );

  const createPublishedPostMenu = useCallback(
    (post: PostItemFragment, statConditions: statisticsConditionType, url: URL | null): ContainerListItem[] => {
      return [
        ...(url && post.actualPublishMethod === 'DIRECT'
          ? [{ label: 'View published post', action: () => url && window.open(url.href, '_blank') }]
          : []),
        // Disable rescheduling from the Queue view as its possible to keep rescheduling posts over and over
        // in the same family which doesn't make sense.
        // { label: 'Reschedule', action: () => duplicateOrReschedulePost(post, 'reschedule') },
        { label: 'Duplicate', action: () => duplicateOrReschedulePost(post, 'duplicate') },
        ...(statConditions.show ? [{ label: 'Detailed Stats', action: () => showDetailsModalHandler(post.id) }] : []),
        ...getBitlyMenuItems(post.link)
      ];
    },
    [duplicateOrReschedulePost, showDetailsModalHandler]
  );

  const createDraftOrActivePostMenu = useCallback(
    (
      post: PostItemFragment,
      statConditions: statisticsConditionType
    ): Array<ContainerListItem | ContainerListItemExtended> => {
      const queuesPaused = selectedWorkspaceSettings?.queuesPaused;
      const isProfilePausedForCategory = isSocialProfilePausedForCategory(post.socialProfile.id, post.category.id);
      const isCategoryOrSocialProfilePaused =
        isCategorySeasonallyPaused(post.category.id) || isProfilePausedForCategory;

      return [
        { label: 'Preview Post', action: () => setShowPostPreview({ post, editPost }) },
        { label: 'Edit', action: () => editPost(post) },
        ...(filters.status === QueueFilterPostStatus.UNQUEUED && post.timing === PostTiming.QUEUE
          ? [
              {
                label: 'Add Timeslot',
                action: () =>
                  navigate(
                    { pathname: '/publish/plan' },
                    { state: { category: post.category, profile: post.socialProfile } }
                  )
              }
            ]
          : []),

        ...((isCategoryOrSocialProfilePaused || queuesPaused) && post.status !== PostStatus.DRAFT
          ? [{ label: 'Unpause', action: () => unPausePosts(post) }]
          : []),
        { label: 'Duplicate', action: () => duplicateOrReschedulePost(post, 'duplicate') },
        ...(post.timing === PostTiming.QUEUE && filters.status === QueueFilterPostStatus.SCHEDULED
          ? [{ label: 'Move to Top', action: () => movePostPosition(post.id, PostQueuePosition.TOP) }]
          : []),
        ...(post.timing === PostTiming.QUEUE && filters.status === QueueFilterPostStatus.SCHEDULED
          ? [{ label: 'Move to Bottom', action: () => movePostPosition(post.id, PostQueuePosition.BOTTOM) }]
          : []),
        ...(statConditions.show ? [{ label: 'Past stats', action: () => showDetailsModalHandler(post.id) }] : []),
        ...getBitlyMenuItems(post.link),
        {
          label: 'Trash',
          action: () => deleteSinglePost(post.id),
          ...(showOnboarding && disableDelete && post.onboardingContent
            ? { disabled: true, disabledTooltipText: 'You’ll be able to delete these demo posts later.' }
            : {})
        }
      ];
    },
    [
      deleteSinglePost,
      duplicateOrReschedulePost,
      editPost,
      filters.status,
      isCategorySeasonallyPaused,
      isSocialProfilePausedForCategory,
      movePostPosition,
      navigate,
      selectedWorkspaceSettings?.queuesPaused,
      showDetailsModalHandler,
      unPausePosts,
      showOnboarding,
      disableDelete
    ]
  );
  const createFailedPostMenu = useCallback(
    (post: PostItemFragment, statConditions: statisticsConditionType): ContainerListItem[] => {
      return [
        { label: 'Reschedule', action: () => duplicateOrReschedulePost(post, 'reschedule') },
        ...(statConditions.show ? [{ label: 'Past stats', action: () => showDetailsModalHandler(post.id) }] : []),
        ...getBitlyMenuItems(post.link),
        { label: 'Trash', action: () => deleteSinglePost(post.id) }
      ];
    },
    [deleteSinglePost, duplicateOrReschedulePost, showDetailsModalHandler]
  );

  const createTrashPostMenu = useCallback(
    (post: PostItemFragment, statConditions: statisticsConditionType): ContainerListItem[] => {
      return [
        {
          label: 'Restore',
          action: () => {
            if (isTemporallySuspended) {
              setSuspendedModalOpen(true);
              return;
            }
            setPostEditorContext({
              type: PostEditorContextEnum.restore_trashed,
              postId: post.id,
              onClose: success => {
                if (success) {
                  setTriggerPostReFetch(post.id);
                }
              }
            });
          }
        },
        ...(statConditions.show ? [{ label: 'Past stats', action: () => showDetailsModalHandler(post.id) }] : []),
        ...getBitlyMenuItems(post.link),
        { label: 'Permanently Delete', action: () => deleteSinglePost(post.id) }
      ];
    },
    [
      setPostEditorContext,
      setTriggerPostReFetch,
      showDetailsModalHandler,
      isTemporallySuspended,
      setSuspendedModalOpen,
      deleteSinglePost
    ]
  );

  const getActionMenuListItems = useCallback(
    (post: PostItemFragment): ContainerListItem[] => {
      const url = getUrlParams(post?.publishedUrl ?? null);
      const statConditions = statisticsCondition(post, undefined, undefined, true);

      switch (post.status) {
        case PostStatus.PUBLISHED:
          return createPublishedPostMenu(post, statConditions, url);
        case PostStatus.ACTIVE:
        case PostStatus.DRAFT:
          return createDraftOrActivePostMenu(post, statConditions);
        case PostStatus.FAILED:
          return createFailedPostMenu(post, statConditions);
        case PostStatus.TRASH:
          return createTrashPostMenu(post, statConditions);
        default:
          return [];
      }
    },
    [createPublishedPostMenu, createDraftOrActivePostMenu, createFailedPostMenu, createTrashPostMenu]
  );

  const pauseAllHandler = (pause: boolean) => {
    if (selectedWorkspaceId) {
      // Set loading now, and let the refetch trigger handle removing it
      setLoadingInitial(true);

      props
        .updateWorkspacePauseSettings({
          variables: { workspaceId: selectedWorkspaceId, input: { pauseQueues: pause } }
        })
        .then(() => {
          setRefetch(true);
        })
        .catch((error: ApolloError) => {
          // If we fail, make sure loader is turned off
          setLoadingInitial(false);
          showToast({
            severity: 'error',
            summary: 'Could not pause your Queue',
            detail: remapApolloErrors(error)
          });
        });
    }
  };

  const shufflePostsHandler = useCallback(() => {
    if (selectedWorkspaceId) {
      // The queueFilters object has extra variables that make the mutation puke, so we need a direct filter input var.
      const mutationFilterVars: QueueFilterInput = {
        categoryId: queueFilters.categoryId,
        content: queueFilters.content,
        feedType: queueFilters.feedType,
        searchTerm: queueFilters.searchTerm,
        socialProfileId: queueFilters.socialProfileId,
        source: queueFilters.source,
        status: queueFilters.status,
        timing: queueFilters.timing
      };
      shuffleQueue({
        variables: { workspaceId: selectedWorkspaceId, filters: mutationFilterVars },
        update: () => {
          updateAllCheckBoxValues('postItemCheckBox', false);
          setTriggerReShuffleFetch(true);
        }
      }).catch((error: ApolloError) => {
        showToast({
          severity: 'error',
          summary: 'Could not shuffle your Queue',
          detail: remapApolloErrors(error)
        });

        // Make sure to bring back the view so user does not get 'infinite loading'
        setLoadingInitial(false);
      });
    }
  }, [
    queueFilters,
    selectedWorkspaceId,
    setLoadingInitial,
    setTriggerReShuffleFetch,
    shuffleQueue,
    updateAllCheckBoxValues
  ]);

  const postIterations = props.postIterationsResults.data?.postIterations || [];

  useEffect(() => {
    if (selectedWorkspaceId) {
      getCategoryProfilesQueueMeta({
        variables: { workspaceId: selectedWorkspaceId }
      }).then(() => {
        // ...
      });
    }
  }, [getCategoryProfilesQueueMeta, selectedWorkspaceId]);

  useEffect(() => {
    setOrientation(() =>
      width < BreakpointsValuesEnum.tabletportrait ? CardOrientationEnum.PORTRAIT : CardOrientationEnum.LANDSCAPE
    );
    setShowFilterModal(() => false); // should we close on resize?
    setHideFilters(() => width < BreakpointsValuesEnum.xlarge);
  }, [width]);

  useEffect(() => {
    updateAllCheckBoxValues('postItemCheckBox', false);
    // Get current form values
    const currentValues = formInstance.getValues();
    const checkboxes = currentValues.postItemCheckBox;

    if (checkboxes && checkboxes.length > 0) {
      // Create a new object without postItemCheckBox
      const updatedValues = { ...currentValues, postItemCheckBox: [] };

      // This is essence unregisters the postItemCheckBox
      formInstance.reset(updatedValues);
    }
  }, [context, updateAllCheckBoxValues, queueFilters, formInstance]);

  // If in Week view, we filter other than Scheduled Status, switch to list view
  useEffect(() => {
    if (context === PostScheduleViewContext.WEEK && status !== QueueFilterPostStatus.SCHEDULED) {
      setContext(PostScheduleViewContext.LIST);
      setOpenDetailsModal(false);
    }
  }, [context, setContext, status]);

  // Always make sure the term is set to the correct value when filters change
  // this also fixes the issue of field reset on card drag
  useEffect(() => {
    formInstance.setValue('term', searchTerm);
  }, [formInstance, searchTerm, queueFilters]);

  useEffect(() => {
    if (selectedWorkspaceId) {
      setFilterLocalStorage(selectedWorkspaceId, filterGroup, PostScheduleFiltersEnum.postScheduleStatusFilter, status);
    }
  }, [filterGroup, selectedWorkspaceId, status]);

  useEffect(() => {
    if (selectedWorkspaceId) {
      setFilterLocalStorage(
        selectedWorkspaceId,
        filterGroup,
        PostScheduleFiltersEnum.postScheduleProfileFilter,
        profile
      );
    }
  }, [selectedWorkspaceId, profile, filterGroup]);

  useEffect(() => {
    if (selectedWorkspaceId) {
      setFilterLocalStorage(
        selectedWorkspaceId,
        filterGroup,
        PostScheduleFiltersEnum.postScheduleCategoryFilter,
        category
      );
    }
  }, [selectedWorkspaceId, category, filterGroup]);

  useEffect(() => {
    if (selectedWorkspaceId && context !== PostScheduleViewContext.LIBRARY) {
      setFilterLocalStorage(selectedWorkspaceId, filterGroup, PostScheduleFiltersEnum.postScheduleTimingFilter, timing);
    }
  }, [context, filterGroup, selectedWorkspaceId, timing]);

  useEffect(() => {
    if (selectedWorkspaceId && context !== PostScheduleViewContext.LIBRARY) {
      setFilterLocalStorage(
        selectedWorkspaceId,
        filterGroup,
        PostScheduleFiltersEnum.postScheduleEvergreenFilter,
        evergreen
      );
    }
  }, [context, filterGroup, selectedWorkspaceId, evergreen]);

  useEffect(() => {
    if (selectedWorkspaceId && context !== PostScheduleViewContext.LIBRARY) {
      setFilterLocalStorage(selectedWorkspaceId, filterGroup, PostScheduleFiltersEnum.postScheduleSourceFilter, source);
    }
  }, [context, filterGroup, selectedWorkspaceId, source]);

  useEffect(() => {
    if (selectedWorkspaceId && context !== PostScheduleViewContext.LIBRARY) {
      setFilterLocalStorage(
        selectedWorkspaceId,
        filterGroup,
        PostScheduleFiltersEnum.postScheduleContentFilter,
        content
      );
    }
  }, [selectedWorkspaceId, content, context, filterGroup]);

  useEffect(() => {
    setSearchTerm(() => '');
  }, [selectedWorkspaceId, context]);

  useEffect(() => {
    dispatchFilters({ type: 'switchFilterType', payload: { data: filtersInitialState, fetchPosts: true } });
  }, [filtersInitialState]);

  // watch for the location state. if we have passed it via notifications menu, and if yes, set filters
  useEffect(() => {
    if (locationState && isPostScheduleFilterType(locationState)) {
      dispatchFilters({ type: 'switchFilterType', payload: { data: locationState, fetchPosts: true } });
    }
  }, [locationState]);

  const viewProps: PostScheduleViewProps = {
    ...props,
    isQueuePaused: selectedWorkspaceSettings?.queuesPaused ?? false,
    postIterations,
    startDate: dateRanges.startDate,
    endDate: dateRanges.endDate,
    setDateRange,
    orientation,
    onSubmit,
    setSearchTerm,
    hideFilters,
    setOrientation,
    forceListView,
    filters,
    dispatchFilters,
    width,
    showDetailsModalHandler,
    actOnAllPosts,
    actionFocus,
    setActionFocus,
    deletePosts,
    pauseAllHandler,
    shufflePostsHandler,
    updatePostLocation,
    movePostPosition,
    getActionMenuListItems,
    formInstance,
    categoryPauseStatus,
    queueFilters,
    queueModals,
    dayStartOfWeek,
    duplicateOrReschedulePost,
    wsTz,
    selectedCategory,
    showCategoryEditModal,
    setShowCategoryEditModal,
    showPostPreview,
    setShowPostPreview,
    selectedPosts,
    postIdToDelete,
    checkboxes,
    deleteSinglePost,
    setPostIdToDelete
  };

  return <PostScheduleView {...viewProps} />;
};
